import { createRoute } from "@tanstack/react-router";
import { z } from "zod";

import { JobListStatus } from "../../api/generated/zod";
import { brandRoute, isUuid, teamRoute } from "../../platform/routes";
import { JobListScreen } from "./JobListScreen";
import { JobScreen } from "./JobScreen";
import { StudioScreen } from "./StudioScreen";

// `?medium=`: the Image or the Video page of the studio (image by default). `?view=`: its tab.
// `?type=&job=&output=`: start from a delivered image of an earlier job, `?type=&asset=` from a
// library file (the library menu's Edit and Animate; a video's Edit with `type=video.edit`). `?clone=`: fill the
// composer from an earlier job (the detail's "Clone"). `?advanced=`: live's Advanced mode on a
// library image (the library's "Clone"); `?layerize=`, its Layerize mode (the library's
// "Layerize"). Anything malformed is dropped, and the form starts from text.
const generateSearch = z.object({
  medium: z.enum(["image", "video"]).optional().catch(undefined),
  clone: z.uuid().optional().catch(undefined),
  // The studio's tab: the brand's library (the default, as in the live app), My Generations, or
  // the Image page's Ad copy (#234).
  view: z.enum(["library", "generations", "copy"]).optional().catch(undefined),
  // `video.edit` with `asset`: a library video's Edit, on the Video Edit tool (S1c).
  type: z.enum(["image.tweak", "video.image", "video.edit"]).optional().catch(undefined),
  job: z.uuid().optional().catch(undefined),
  // A library file to start from (the library's Edit and Animate, S1 decision 22).
  asset: z.uuid().optional().catch(undefined),
  // A library image the library's Clone names: live's Advanced mode with it as its reference
  // (live's `onImageClone`; issue 828, item 23).
  advanced: z.uuid().optional().catch(undefined),
  // A library image the library's Layerize names: live's Layerize mode on it (G13; live's
  // `onImageLayerize`).
  layerize: z.uuid().optional().catch(undefined),
  output: z.coerce.number().int().min(0).max(15).optional().catch(undefined),
});

/** Jobs start in a brand (G1 decision 2), so the form is the brand's. */
export const generateRoute = createRoute({
  getParentRoute: () => brandRoute,
  path: "generate",
  validateSearch: (search) => generateSearch.parse(search),
  component: StudioScreen,
});

/** A job is read at the team, as its route is (G1). */
export const jobRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "jobs/$jobId",
  params: { parse: ({ jobId }) => (isUuid(jobId) ? { jobId } : false) },
  component: JobScreen,
});

// `?status=&brand=`: malformed values are dropped, and the list shows every job.
const jobListSearch = z.object({
  status: JobListStatus.optional().catch(undefined),
  brand: z.uuid().optional().catch(undefined),
});

/** The team's jobs (G4), beside one job's page. */
export const jobListRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "jobs",
  validateSearch: (search) => jobListSearch.parse(search),
  component: JobListScreen,
});
